import { CkEthMinterCanister, type CkEthMinterDid } from '@icp-sdk/canisters/cketh'
import { Principal } from '@icp-sdk/core/principal'
import React, { createContext, PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { config } from '../appConfig'
import CacheClient, { TokenPriceWithChange } from '../clients/CacheClient'
import HyperLiquidClient from '../clients/HyperLiquidClient'
import {
  CK_MINTER_CANISTER_ID,
  PLUG_REGISTRY_CANISTER_ID,
  SEPOLIA_CK_MINTER_CANISTER_ID,
} from '../constants/canisterIds'
import {
  idlFactory,
  NFT,
  _SERVICE as PlugTokenRegistryFactory,
} from '../idls/plugTokenRegistry/plugTokenRegistry.did'
import {
  AnalyticsProvider,
  Chain,
  EVMChain,
  PlugToken,
  PlugTokenGroup,
  PriceProvider,
  SNS,
} from '../interfaces/common'
import { Perpetual } from '../interfaces/hyperliquid'
import { logger } from '../logger'
import {
  useCurrentWallet,
  useCustomTokens,
  useDevMode,
  useEVMChainsInfo,
  useLocales,
  useProtectedCanisters,
  useSns,
  useTokens,
} from '../state'
import { CachedToken } from '../state/tokens'
import { createActor } from '../utils/createActor'

export interface PerpetualToken extends Perpetual {
  id: number
}

export interface ConfigProviderContextType {
  tokens: PlugToken[]
  tokenGroups: PlugTokenGroup[]
  nfts: NFT[]
  tokenPricesWithChange: TokenPriceWithChange
  activeSns: SNS[]
  ckerc20Tokens?: CkEthMinterDid.CkErc20Token[]
  ethMinterAddress?: string
  erc20MinterAddress?: string
  evmChains: EVMChain[]
  perpsTokens: PerpetualToken[]
}

export interface WeekChartItem {
  t: number
  p: number
}

export const ConfigContext = createContext<ConfigProviderContextType>(null as any)

const getEVMChain = (evmChains: EVMChain[], evmChainId?: number): EVMChain | undefined => {
  if (!evmChainId) return undefined

  return evmChains.find((c) => c.chainId === evmChainId)
}

const DEV_EVM_CHAINS: EVMChain[] = [
  {
    chainId: 11155111,
    name: 'Sepolia',
    symbol: 'SEP',
    rpcEndpoint: `https://sepolia.infura.io/v3/${config.fetchEnv('INFURA_SECRET')}`,
    icon: '',
  },
]

const ConfigProvider = ({ children }: PropsWithChildren) => {
  const [nfts, setNfts] = useState<NFT[]>([])
  const [tokenPricesWithChange, setTokenPricesWithChange] = useState<TokenPriceWithChange>({})
  const [ckerc20Tokens, setCkErc20Tokens] = useState<CkEthMinterDid.CkErc20Token[]>()
  const [ethMinterAddress, setEthMinterAddress] = useState<string>()
  const [erc20MinterAddress, setErc20MinterAddress] = useState<string>()
  const [evmChains, setEVMChains] = useState<EVMChain[]>([])
  const [perpsTokens, setPerpsTokens] = useState<PerpetualToken[]>([])

  const { setSns, sns } = useSns()
  const { setProtectedCanisters } = useProtectedCanisters()
  const { currentWallet } = useCurrentWallet()
  const { customTokens } = useCustomTokens()
  const { activeNetwork, networkTokens, devEnabled } = useDevMode()
  const { setTokens, tokens, tokenGroups, setTokenGroups } = useTokens()
  const { setChains } = useEVMChainsInfo()

  const agent = useMemo(config.getAgent, [])
  const client = useMemo(() => new CacheClient(), [])
  const isSepolia = config.fetchEnv('ETH_CHAIN') === 'sepolia'

  const plugRegistryActor = createActor<PlugTokenRegistryFactory>(idlFactory, {
    canisterId: PLUG_REGISTRY_CANISTER_ID,
    agent,
  })

  useEffect(() => {
    const hyperliquidClient = new HyperLiquidClient()

    const whitelistedPerpsIds = tokens
      .map((t) => t.additionalData.hyperliquidId)
      .filter((id) => !!id)
      .map((id) => parseInt(id))

    hyperliquidClient
      .getPerpetuals()
      .then((perps) =>
        setPerpsTokens(
          perps
            .sort((a, b) => b.maxLeverage - a.maxLeverage)
            .filter((perp) => whitelistedPerpsIds.includes(perp.id))
        )
      )
      .catch(logger.error)
  }, [tokens])

  const getEVMChains = async () => {
    const chains = await plugRegistryActor
      .get_all_evm_chains([config.fetchEnv('REGISTRY_SECRET')])
      .catch(() => [])

    return chains.map((chain) => ({
      chainId: Number(chain.id),
      name: chain.name,
      symbol: chain.symbol,
      rpcEndpoint: chain.rpc_endpoint,
      icon: chain.logo,
      feeTokenId: chain.fee_token_id[0],
    }))
  }

  const updateTokens = async (savedEVMChains: EVMChain[]) => {
    const response = await plugRegistryActor.get_all_tokens().catch(() => [])
    const tokens: CachedToken[] = response.map((token) => {
      return {
        id: token.id,
        chain: token.chain as Chain,
        logo: token.logo_url,
        name: token.name,
        description: token.description,
        address: token.address[0],
        standard: token.standard,
        symbol: token.symbol,
        decimals: token.decimals,
        fee: token.fee[0]?.toString(),
        fee_token_id: token.fee_token_id[0],
        tags: token.tags,
        groupId: token.group_id[0],
        analyticsDetails: {
          analyticsProvider: token.analytics_details.analytics_provider[0] as AnalyticsProvider,
          analyticsId: token.analytics_details.analytics_id[0],
          priceProvider: token.analytics_details.price_provider[0] as PriceProvider,
          priceId: token.analytics_details.price_id[0],
        },
        additionalData: Object.fromEntries(token.additional_data.map((d) => [d.kind, d.value])),
        evmChain: getEVMChain(savedEVMChains, parseInt(token.chain)),
      }
    })

    setProtectedCanisters(tokens.filter((t) => t.chain === Chain.ICP).map((t) => t.address!))
    setTokens(tokens)

    const groupResponse = await plugRegistryActor.get_token_groups().catch(() => [])
    const groups: PlugTokenGroup[] = groupResponse.map((group) => {
      return {
        id: group.id,
        name: group.name,
        logoUrl: group.logo_url,
        symbol: group.symbol,
        analyticsDetails: {
          analyticsProvider: group.analytics_details.analytics_provider[0] as AnalyticsProvider,
          analyticsId: group.analytics_details.analytics_id[0],
          priceProvider: group.analytics_details.price_provider[0] as PriceProvider,
          priceId: group.analytics_details.price_id[0],
        },
      }
    })

    setTokenGroups(groups)

    const registryNfts = await plugRegistryActor.get_all_nfts().catch(() => [])
    setNfts(registryNfts)
  }

  const walletCustomTokens = useMemo(() => {
    if (devEnabled) {
      return networkTokens[activeNetwork || ''] || []
    }

    return customTokens[currentWallet] || []
  }, [currentWallet, customTokens, devEnabled, activeNetwork, networkTokens])

  const allTokens = useMemo(() => {
    if (devEnabled) return walletCustomTokens

    return [
      ...tokens.map((t) => ({ ...t, fee: t.fee ? BigInt(t.fee) : 0n })),
      ...walletCustomTokens,
    ]
  }, [tokens, walletCustomTokens])

  const { setLocales } = useLocales()

  const refresh = async (promise: Promise<any>, setter: (val: any) => void) => {
    try {
      setter(await promise)
    } catch (e) {
      logger.debug(e as Error)
    }
  }

  const refreshData = async () => {
    refresh(client.getTokenPrices(), setTokenPricesWithChange)
    refresh(client.getSns(), setSns)
    refresh(client.getLocales(), setLocales)
    refreshEthData()

    const evmChains = await getEVMChains()

    setChains(evmChains)
    setEVMChains(evmChains)
    updateTokens(evmChains).catch(logger.debug)
  }

  const refreshEthData = async () => {
    const minterCanisterId = isSepolia ? SEPOLIA_CK_MINTER_CANISTER_ID : CK_MINTER_CANISTER_ID
    const minterCanister = CkEthMinterCanister.create({
      canisterId: Principal.from(minterCanisterId),
      agent: config.getAgent(),
    })

    try {
      const minterInfo = await minterCanister.getMinterInfo({ certified: true })

      setCkErc20Tokens(minterInfo.supported_ckerc20_tokens?.[0] || [])
      setErc20MinterAddress(minterInfo.erc20_helper_contract_address?.[0])
      setEthMinterAddress(minterInfo.eth_helper_contract_address?.[0])
    } catch {
      setCkErc20Tokens([])
    }
  }

  useEffect(() => {
    refreshData()

    const interval = setInterval(refreshData, 60 * 60 * 1000)

    return () => clearInterval(interval)
  }, [])

  const value = useMemo(() => {
    return {
      tokens: allTokens,
      tokenGroups,
      tokenPricesWithChange,
      activeSns: sns,
      nfts,
      ckerc20Tokens,
      ethMinterAddress,
      erc20MinterAddress,
      evmChains: devEnabled ? DEV_EVM_CHAINS : evmChains,
      perpsTokens,
    }
  }, [
    allTokens,
    tokenGroups,
    tokenPricesWithChange,
    sns,
    nfts,
    ckerc20Tokens,
    ethMinterAddress,
    erc20MinterAddress,
    evmChains,
    devEnabled,
    perpsTokens,
  ])

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
}

export default ConfigProvider
